import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import { S } from "../../styles";
import { LoadingState } from "../../components/QueryStates";
import { AuditEventTable } from "./AuditTrail";

const RESOURCE_TYPES = [
  "rfq",
  "quote",
  "purchase_order",
  "requirement",
  "supplier",
  "part",
  "shipment",
  "user",
  "tenant",
];

export function AuditLogPanel() {
  const [resourceType, setResourceType] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["audit-events-list", skip, resourceType],
    queryFn: () =>
      api.listAuditEvents({
        skip,
        limit,
        resourceType: resourceType || undefined,
      }),
  });

  const events = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap" as const,
          gap: 10,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>
          🕓 Audit Log{" "}
          <span style={{ fontSize: 14, fontWeight: 400, color: "#888" }}>
            ({total} total)
          </span>
        </h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            style={{ ...S.select, width: "auto", marginBottom: 0 }}
            value={resourceType}
            onChange={(e) => {
              setResourceType(e.target.value);
              setSkip(0);
            }}
          >
            <option value="">All resource types</option>
            {RESOURCE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <button style={S.btnSm} onClick={() => void refetch()}>
            ↺ Refresh
          </button>
        </div>
      </div>

      <p style={{ ...S.muted, marginTop: 0, marginBottom: 16, fontSize: 13 }}>
        Immutable record of every user and system action across this tenant.
        Click a row to inspect the before/after changes, IP, and client.
      </p>

      {error && (
        <div style={S.card}>
          <p style={S.err}>⚠ {error.message}</p>
        </div>
      )}

      {isLoading && <LoadingState label="Loading audit events…" />}

      {!isLoading && events.length === 0 && !error && (
        <div style={S.card}>
          <p style={S.muted}>No audit events recorded yet.</p>
        </div>
      )}

      {events.length > 0 && <AuditEventTable events={events} />}

      {total > limit && (
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 12,
            alignItems: "center",
          }}
        >
          <button
            style={S.btnGhost}
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - limit))}
          >
            ← Prev
          </button>
          <span style={S.muted}>
            {skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            style={S.btnGhost}
            disabled={skip + limit >= total}
            onClick={() => setSkip(skip + limit)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
